import { useState, useEffect, useRef } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { NATIONALITIES, isValidNationality } from "@/lib/nationalities";

/**
 * Combobox de nationalité (France en première position, recherche live).
 *
 * Props:
 *  - value : valeur courante (string)
 *  - onChange(newValue) : appelée avec la valeur retenue (toujours dans
 *    NATIONALITIES) ou la chaîne vide si effacée.
 *  - placeholder : optionnel
 *  - required : ajoute le `data-required` (visuel d'erreur géré par le parent)
 *  - invalid : flag externe pour forcer le style "bordure rouge"
 *  - dataTestId : prefix data-testid (par défaut "nationality")
 *  - inputClassName : classes injectées sur l'input
 */
export default function NationalitySelect({
  value,
  onChange,
  placeholder = "Sélectionner une nationalité…",
  required = false,
  invalid = false,
  dataTestId = "nationality",
  inputClassName = "",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef(null);

  // Sync : si la valeur change extérieurement, mettre à jour le champ.
  useEffect(() => { setSearch(""); }, [value]);

  const filtered = NATIONALITIES.filter((n) =>
    !search.trim() || n.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSelect = (nat) => {
    onChange(nat);
    setSearch("");
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
    setSearch("");
  };

  // Si l'utilisateur ferme le popover en ayant saisi un texte qui matche
  // exactement une nationalité, on l'accepte (ergonomie clavier).
  const handleOpenChange = (next) => {
    if (!next && search) {
      const exact = NATIONALITIES.find((n) => n.toLowerCase() === search.trim().toLowerCase());
      if (exact) onChange(exact);
      setSearch("");
    }
    setOpen(next);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <div className="relative cursor-pointer" data-testid={`${dataTestId}-trigger`}>
          <Input
            ref={inputRef}
            value={open ? search : (value || "")}
            onChange={(e) => { setSearch(e.target.value); if (!open) setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className={`rounded-none pr-9 ${invalid ? "border-red-500 focus-visible:ring-red-300" : ""} ${inputClassName}`}
            data-testid={`${dataTestId}-input`}
            aria-required={required}
          />
          {value && !open && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-8 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 rounded-sm"
              tabIndex={-1}
              data-testid={`${dataTestId}-clear`}
              aria-label="Effacer la nationalité"
            >
              <X className="w-3.5 h-3.5 text-gray-400" />
            </button>
          )}
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)] max-h-72 overflow-y-auto rounded-none"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {filtered.length === 0 ? (
          <p className="text-xs text-gray-500 italic px-3 py-4 text-center" data-testid={`${dataTestId}-no-match`}>
            Aucune nationalité ne correspond à « {search} ». Le pays doit exister dans la liste.
          </p>
        ) : (
          <ul className="py-1" role="listbox">
            {filtered.map((nat) => {
              const isSelected = value && nat.toLowerCase() === value.toLowerCase();
              return (
                <li key={nat}>
                  <button
                    type="button"
                    onClick={() => handleSelect(nat)}
                    className={`w-full text-left px-3 py-1.5 text-sm hover:bg-[#F0E9F3] flex items-center gap-2 ${isSelected ? "bg-[#F0E9F3] text-[#662483] font-semibold" : "text-[#3F3F3F]"}`}
                    data-testid={`${dataTestId}-option-${nat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <Check className={`w-3.5 h-3.5 ${isSelected ? "opacity-100 text-[#662483]" : "opacity-0"}`} />
                    <span>{nat}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}

export { isValidNationality };
